import { useEffect, useRef, useState } from 'react'

import { faChevronDown, faUserCircle } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Box,
  Button,
  ClickAwayListener,
  List,
  ListItem,
  ListItemText,
  Paper,
  Popper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import Avatar from '@scoir/avatar'

import { useHistory } from 'components/ReactRouterProvider/context'

type NavItem = {
  path?: string
  label: string
  externalPath?: string
  onClick?: () => void
}

const useStyles = makeStyles((theme) => ({
  container: {
    borderRight: '1px solid #ccc',
  },
  button: {
    // Match header navigation focus
    // Reconsider when header navigation is refactored
    padding: theme.spacing(0, 1),
    marginRight: 2,
    borderRadius: 4,
  },
  popper: {
    zIndex: 1000,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 260,
    borderRadius: 4,
    padding: 0,
    '& .MuiListItem-root.MuiListItem-button.Mui-selected': {
      backgroundColor: theme.palette.pageBackground,
    },
  },
  item: {
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
  userIcon: {
    color: `#ccc`,
  },
  welcome: {
    color: `#333`,
  },
}))

export default function UserNavigation({
  userName,
  userAvatarUrl,
  settingsItems,
}: {
  userName?: string
  userAvatarUrl?: string
  settingsItems: NavItem[]
}) {
  const classes = useStyles()
  const theme = useTheme()
  const isSmallScreenSize = useMediaQuery(theme.breakpoints.down('sm'))
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const history = useHistory()

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen)
  }

  const handleClose = (event: React.MouseEvent<EventTarget>) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return
    }

    setOpen(false)
  }

  const handleNavigation = (item: NavItem) => {
    // Close the menu
    setOpen(false)

    //    Navigate to the selected item
    if (item.path) history.replace(item.path)
    // Open External Link
    if (item.externalPath) {
      window.open(item.externalPath, '_blank')
    }
    // Perform onClick action
    if (item.onClick) {
      item.onClick()
    }
  }

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = useRef(open)
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current!.focus()
    }

    prevOpen.current = open
  }, [open])

  return (
    <Box className={classes.container}>
      <Button
        ref={anchorRef}
        aria-controls={open ? 'nav-menu' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        className={classes.button}
      >
        <Box
          display="flex"
          alignItems="center"
          gridGap={8}
        >
          <Avatar
            aria-label="user icon"
            variant="circular"
            size="sm"
            sourceUrl={userAvatarUrl}
            fallbackIcon={
              <FontAwesomeIcon
                size="xl"
                icon={faUserCircle}
                className={classes.userIcon}
              />
            }
          />
          {!isSmallScreenSize && (
            <>
              <Typography
                variant="body2"
                className={classes.welcome}
              >
                Welcome {userName}
              </Typography>
              <FontAwesomeIcon
                icon={faChevronDown}
                className={classes.welcome}
                size="xs"
              />
            </>
          )}
        </Box>
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-start"
        transition
        disablePortal
        className={classes.popper}
      >
        <Paper>
          <ClickAwayListener onClickAway={handleClose}>
            <List
              dense
              id="nav-menu"
              className={classes.list}
            >
              {settingsItems.map((item) => (
                <ListItem
                  button
                  key={item.label}
                  className={classes.item}
                  onClick={() => handleNavigation(item)}
                  selected={Boolean(item.path && window.location.pathname.includes(item.path))}
                >
                  <ListItemText>{item.label}</ListItemText>
                </ListItem>
              ))}
            </List>
          </ClickAwayListener>
        </Paper>
      </Popper>
    </Box>
  )
}
